import React, { useMemo, useState, Children } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProductCard } from '../components/product/ProductCard';
import { FilterPanel } from '../components/filters/FilterPanel';
import { FilterGroup, CheckboxFilter, PriceRangeFilter } from '../components/filters/FilterGroup';
import { Select } from '../components/ui/Select';
import { getCategoryBySlug } from '../data/categories';
import { getProductsByCategory, products } from '../data/products';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
type SortOption = 'popular' | 'new' | 'price-asc' | 'price-desc';
export function CategoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const category = getCategoryBySlug(slug || '');
  const categoryProducts = slug ? getProductsByCategory(slug) : [];
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100000]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  // Get all products for this category (or all products if no category)
  const allProducts = category ? categoryProducts : products;
  // Calculate price range
  const minPrice = Math.min(...allProducts.map(p => p.price));
  const maxPrice = Math.max(...allProducts.map(p => p.price));
  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];
    // Filter by price
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    // Filter by stock
    if (inStockOnly) {
      result = result.filter(p => p.inStock);
    }
    // Sort
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'new':
        result.sort((a, b) => a.badge === 'Новинка' ? -1 : 1);
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [allProducts, sortBy, priceRange, inStockOnly]);
  const handleResetFilters = () => {
    setPriceRange([minPrice, maxPrice]);
    setInStockOnly(false);
    setSelectedBrands([]);
  };
  const sortOptions = [{
    value: 'popular',
    label: 'Популярні'
  }, {
    value: 'new',
    label: 'Нові'
  }, {
    value: 'price-asc',
    label: 'Дешевші'
  }, {
    value: 'price-desc',
    label: 'Дорожчі'
  }];
  if (!category && slug) {
    return <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-id="element-515">
        <div className="text-center" data-id="element-516">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-517">
            Категорію не знайдено
          </h1>
          <Link to="/catalog" className="text-blue-500 hover:text-blue-400" data-id="element-518">
            Повернутися до каталогу
          </Link>
        </div>
      </main>;
  }
  return <main className="min-h-screen bg-[#0a0a0a]" data-id="element-519">
      {/* Breadcrumbs */}
      <div className="bg-[#0a0a0a] border-b border-[#1a1a1a]" data-id="element-520">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-521">
          <nav className="flex items-center gap-2 text-sm" data-id="element-522">
            <Link to="/" className="text-gray-500 hover:text-gray-300" data-id="element-523">
              Головна
            </Link>
            <span className="text-gray-700" data-id="element-524">/</span>
            <Link to="/catalog" className="text-gray-500 hover:text-gray-300" data-id="element-525">
              Каталог
            </Link>
            {category && <>
                <span className="text-gray-700" data-id="element-526">/</span>
                <span className="text-white font-medium" data-id="element-527">{category.name}</span>
              </>}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-528">
        {/* Header */}
        <div className="mb-8" data-id="element-529">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2" data-id="element-530">
            {category?.name || 'Всі товари'}
          </h1>
          {category && <p className="text-gray-400" data-id="element-531">{category.description}</p>}
        </div>

        <div className="flex gap-8" data-id="element-532">
          {/* Filters Sidebar */}
          <FilterPanel onReset={handleResetFilters} className="w-64 flex-shrink-0" data-id="element-533">
            <FilterGroup title="Ціна" data-id="element-534">
              <PriceRangeFilter min={minPrice} max={maxPrice} value={priceRange} onChange={setPriceRange} data-id="element-535" />
            </FilterGroup>

            <FilterGroup title="Наявність" data-id="element-536">
              <CheckboxFilter label="Тільки в наявності" checked={inStockOnly} onChange={setInStockOnly} data-id="element-537" />
            </FilterGroup>

            <FilterGroup title="Бренд" data-id="element-538">
              <CheckboxFilter label="Intex" checked={false} onChange={() => {}} count={12} data-id="element-539" />
              <CheckboxFilter label="Bestway" checked={false} onChange={() => {}} count={8} data-id="element-540" />
              <CheckboxFilter label="Hayward" checked={false} onChange={() => {}} count={5} data-id="element-541" />
              <CheckboxFilter label="Emaux" checked={false} onChange={() => {}} count={7} data-id="element-542" />
            </FilterGroup>

            <FilterGroup title="Рейтинг" data-id="element-543">
              <CheckboxFilter label="4+ зірки" checked={false} onChange={() => {}} data-id="element-544" />
              <CheckboxFilter label="3+ зірки" checked={false} onChange={() => {}} data-id="element-545" />
            </FilterGroup>
          </FilterPanel>

          {/* Products Grid */}
          <div className="flex-1" data-id="element-546">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4" data-id="element-547">
              <p className="text-sm text-gray-400" data-id="element-548">
                Знайдено{' '}
                <span className="font-semibold text-white" data-id="element-549">
                  {filteredProducts.length}
                </span>{' '}
                товарів
              </p>
              <div className="flex items-center gap-4" data-id="element-550">
                <Select options={sortOptions} value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="w-40" data-id="element-551" />
              </div>
            </div>

            {/* Products */}
            {filteredProducts.length > 0 ? <motion.div initial="hidden" animate="visible" variants={containerVariants} className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-552">
                {filteredProducts.map(product => <motion.div key={product.id} variants={itemVariants} data-id="element-553">
                    <ProductCard product={product} data-id="element-554" />
                  </motion.div>)}
              </motion.div> : <div className="text-center py-16 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl" data-id="element-555">
                <p className="text-gray-400 mb-4" data-id="element-556">
                  Товарів за вашим запитом не знайдено
                </p>
                <button onClick={handleResetFilters} className="text-blue-500 hover:text-blue-400 font-medium" data-id="element-557">
                  Скинути фільтри
                </button>
              </div>}
          </div>
        </div>
      </div>
    </main>;
}